import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Saturation, Hue } from 'react-color/lib/components/common';
import { ChromePointer } from 'react-color/lib/components/chrome/ChromePointer';
import { ChromePointerCircle } from 'react-color/lib/components/chrome/ChromePointerCircle';
import tinycolor2 from 'tinycolor2';
import { Swatch } from 'react-color/lib/components/common/Swatch';
import { ColorPickerFields } from './ColorPickerFields';
import { ColorResult } from './types';
import BrandKitColorPicker from './BrandKitColorPicker';
import Icon from '../Icon';
import { classnames } from '@/libs/utils';
import { useDynamicPopupPosition } from '@/hooks/useDynamicPopupPosition';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import { useRemoteApp } from '@/context/RemoteAppContext/RemoteAppContext';

interface ColorPickerProps {
  hex: string;
  children?: React.ReactNode;
  pickerClassNames?: string;
  classNames?: string;
  openClassNames?: string;
  defaultColors?: string[];
  defaultColorsLabel?: string;
  onChange: (hex: string) => void;
  onClose?: () => void;
  useDynamicPosition?: boolean;
  /** Intercepts the trigger click; parent popover takes over opening. */
  onOpen?: () => void;
  /** Renders content inline (no trigger, no floating panel). */
  inline?: boolean;
  /** Shows a ← back button when inline is true. */
  onBack?: () => void;
}

function LegacyColorPicker({
  hex,
  classNames = '',
  openClassNames = '',
  pickerClassNames = '',
  children = <></>,
  defaultColors = [],
  defaultColorsLabel = '',
  onChange,
  onClose,
  useDynamicPosition = false
}: ColorPickerProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const colorPicker = useRef<HTMLDivElement>(null);
  // Child #1 - Picker in closed state
  // Child #2 - default colors placeholder
  const childrenArray = React.Children.toArray(children);
  const [isOpened, setIsOpened] = useState(false);
  const [hsl, setHsl] = useState();
  const [hsv, setHsv] = useState();
  const [rgb, setRgb] = useState();
  const [pickerColor, setPickerColor] = useState('white');

  // Use dynamic positioning to prevent cropping on smaller screens
  const { position, isPositioned } = useDynamicPopupPosition({
    triggerRef,
    popupRef: colorPicker,
    isVisible: isOpened && useDynamicPosition,
    preferredPosition: 'right',
    offset: 8,
    viewportPadding: 16
  });

  useEffect(() => {
    setHsv(tinycolor2(hex).toHsv());
    setHsl(tinycolor2(hex).toHsl());
    setRgb(tinycolor2(hex).toRgb());
    setPickerColor(!!hex && tinycolor2(hex).isDark() ? 'white' : 'black');
  }, [hex]);

  function colorChanged(val: ColorResult) {
    onChange(`#${tinycolor2(val).toHex()}`);
  }

  function hueChanged(val: ColorResult) {
    const hexColor = tinycolor2(val).toHex();
    if (hexColor === 'ffffff' || hexColor === '000000') {
      // Override color picker default behavior, since there is issue with Hue when color is black or white
      val.s = 0.5;
      val.l = 0.2;
    }
    onChange(`#${tinycolor2(val).toHex()}`);
  }

  useEffect(() => {
    if (isOpened) {
      document.addEventListener('pointerup', pointerUp);
    }
  }, [isOpened]);

  function pointerUp(e) {
    if (!colorPicker.current?.contains(e.target)) {
      setIsOpened(false);
      onClose?.();
      document.removeEventListener('pointerup', pointerUp);
    }
  }

  const openPicker = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsOpened(true);
    },
    [setIsOpened]
  );

  return (
    <>
      <div
        ref={triggerRef}
        className={classnames(
          'size-full cursor-pointer border border-slate-200',
          classNames,
          isOpened ? openClassNames : ''
        )}
        data-testid="color-picker"
        style={{ backgroundColor: hex, color: pickerColor }}
        onClick={openPicker}
      >
        {childrenArray?.[0]}
      </div>
      {isOpened ? (
        <React.Fragment>
          <div
            className={classnames(useDynamicPosition ? 'fixed' : 'absolute', 'z-50', pickerClassNames)}
            ref={colorPicker}
            style={{
              ...(useDynamicPosition ? position : {}),
              opacity: useDynamicPosition ? (isPositioned ? 1 : 0) : 1,
              transition: 'opacity 150ms ease-in-out'
            }}
          >
            <div className="w-[225px] rounded-lg bg-white shadow-xl">
              <div className="relative h-[180px] w-full cursor-pointer overflow-hidden rounded-se-xl rounded-ss-xl pb-[55%]">
                <Saturation hsl={hsl} hsv={hsv} pointer={ChromePointerCircle} onChange={colorChanged} />
              </div>
              <div className="p-3">
                <div className="flex">
                  <div className="flex-1">
                    <div className="relative h-[10px] cursor-pointer">
                      <Hue hsl={hsl} pointer={ChromePointer} onChange={hueChanged} />
                    </div>
                  </div>
                </div>
                <ColorPickerFields rgb={rgb} hsl={hsl} hex={hex} onChange={colorChanged} />
                <div className="relative">
                  {defaultColorsLabel && (!!defaultColors.length || childrenArray.length > 1) && (
                    <>
                      <div className="relative top-3 z-[1] mx-auto flex max-w-[80%] items-center justify-center space-x-0.5 rounded-full border bg-white px-3 py-1">
                        <Icon name="IconColorSwatch" size={16} stroke={2} />
                        <span className="text-xs">{defaultColorsLabel}</span>
                      </div>
                      <div className="max-h-[80px] overflow-y-auto border-t pt-5">
                        {defaultColors.length
                          ? defaultColors.map(c => (
                              <Swatch
                                key={c}
                                color={c}
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  float: 'left',
                                  marginRight: '4px',
                                  borderRadius: '9999px',
                                  borderWidth: '1px',
                                  borderColor: '#e2e8f0'
                                }}
                                onClick={colorChanged}
                                focusStyle={{
                                  boxShadow: `0 0 4px ${c}`
                                }}
                              />
                            ))
                          : childrenArray?.[1]}
                      </div>
                    </>
                  )}
                  <div className="clear-both" />
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      ) : (
        <></>
      )}
    </>
  );
}

export const ColorPicker = ({ ...props }: ColorPickerProps): JSX.Element => {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const usePlatformBrandKit = !!featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const { isRemoteApp } = useRemoteApp();

  // When used as remote app - brand kit is not available
  if (usePlatformBrandKit && !isRemoteApp) {
    const childrenArray = React.Children.toArray(props.children);
    return (
      <BrandKitColorPicker
        selectedColor={props.hex || '#000000'}
        onColorSelect={props.onChange}
        title={props.defaultColorsLabel}
        classNames={props.classNames}
        openClassNames={props.openClassNames}
        pickerClassNames={props.pickerClassNames}
        useDynamicPosition={props.useDynamicPosition}
        onClose={props.onClose}
        onOpen={props.onOpen}
        inline={props.inline}
        onBack={props.onBack}
      >
        {childrenArray[0]}
      </BrandKitColorPicker>
    );
  }

  return <LegacyColorPicker {...props} defaultColorsLabel={isRemoteApp ? undefined : props.defaultColorsLabel} />;
};
